import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Armchair, ChevronDown, ChevronUp, X, UserMinus, Users } from 'lucide-react';
import {
  ApiError,
  api,
  canManageUsers,
  type Alliance,
  type AuthUser,
  type Player,
  type TransferAllianceOverviewRow,
  type TransferPlayer,
  type TransferSeatBucket,
  type TransferSeatColor,
  type TransferStats,
  type TransferTagStat,
  type TransferWindowDetail,
} from '@/lib/api';
import { tagChipClasses } from '@/lib/tagColor';
import { SEAT_COLOR_ORDER, SEAT_COLOR_STYLE } from '@/lib/seatColor';

type SortKey =
  | 'name'
  | 'source'
  | 'alliance'
  | 'thp'
  | 'confirmed'
  | 'accepted'
  | 'seat'
  | 'added';

const SEAT_SORT_INDEX: Record<TransferSeatColor, number> = {
  gold: 1,
  purple: 2,
  blue: 3,
  white: 4,
};

export default function AdminTransferWindowDetail({ user }: { user: AuthUser }) {
  const { id } = useParams<{ id: string }>();
  const windowId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['transfer-window', windowId],
    queryFn: () => api.getTransferWindow(windowId),
    enabled: Number.isInteger(windowId) && windowId > 0,
  });
  const alliancesQuery = useQuery({
    queryKey: ['alliances'],
    queryFn: () => api.listAlliances(),
  });
  const playersQuery = useQuery({
    queryKey: ['players', 'active'],
    queryFn: () => api.listPlayers(),
  });

  const [filterText, setFilterText] = useState('');
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('added');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showNotes, setShowNotes] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.deleteTransferWindow(windowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-windows'] });
      qc.invalidateQueries({ queryKey: ['transfer-windows', 'open'] });
      navigate('/admin/transfers');
    },
  });

  const toggleClosed = useMutation({
    mutationFn: (closed: boolean) => api.updateTransferWindow(windowId, { closed }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });
      qc.invalidateQueries({ queryKey: ['transfer-windows'] });
      qc.invalidateQueries({ queryKey: ['transfer-windows', 'open'] });
    },
  });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can manage transfer windows.</div>;
  }

  if (detailQuery.isLoading) return <div className="text-slate-500">Loading…</div>;
  const data = detailQuery.data;
  if (!data) return <div className="text-slate-400">Window not found.</div>;

  const players = data.players;
  const shown = players
    .filter((p) => {
      if (tagFilter.size > 0) {
        for (const t of tagFilter) if (!p.tags.includes(t)) return false;
      }
      if (!filterText.trim()) return true;
      const needle = filterText.trim().toLowerCase();
      return (
        p.name.toLowerCase().includes(needle) ||
        p.sourceServer.toLowerCase().includes(needle) ||
        (p.originAlliance?.toLowerCase().includes(needle) ?? false) ||
        p.tags.some((t) => t.toLowerCase().includes(needle))
      );
    })
    .sort((a, b) => cmp(a, b, sortKey, sortDir));

  const toggleTagFilter = (t: string) =>
    setTagFilter((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'thp' || k === 'added' ? 'desc' : 'asc');
    }
  };

  const isClosed = data.window.closedAt != null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/transfers"
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            ← All windows
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{data.window.name}</h1>
            {isClosed && (
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs uppercase tracking-wider text-slate-400">
                Closed
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400">
            {data.counts.total} players · {data.counts.confirmed} confirmed ·{' '}
            {data.counts.accepted} accepted
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/admin/transfers/${windowId}/kick`}
            className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800"
          >
            Kick candidates
          </Link>
          <button
            onClick={() => toggleClosed.mutate(!isClosed)}
            disabled={toggleClosed.isPending}
            className={
              isClosed
                ? 'rounded border border-emerald-800 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-40'
                : 'rounded border border-amber-800 px-3 py-1 text-xs text-amber-300 hover:bg-amber-900/40 disabled:opacity-40'
            }
          >
            {toggleClosed.isPending
              ? isClosed
                ? 'Reopening…'
                : 'Closing…'
              : isClosed
                ? 'Reopen window'
                : 'Close window'}
          </button>
          <button
            onClick={() => {
              if (
                confirm(
                  `Delete transfer window "${data.window.name}" and all ${data.counts.total} players?`,
                )
              ) {
                remove.mutate();
              }
            }}
            className="rounded border border-red-900 px-3 py-1 text-xs text-red-300 hover:bg-red-900/40"
          >
            Delete window
          </button>
        </div>
      </header>

      {isClosed && (
        <div className="rounded border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm text-slate-400">
          Closed on {data.window.closedAt?.slice(0, 10)}. Reopen to keep working on it.
        </div>
      )}

      <StatsTiles stats={data.stats} />

      <WindowEditor detail={data} />

      <AddPlayerForm windowId={windowId} existingTags={data.tags} />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input max-w-xs"
            placeholder="Filter by name, server, alliance, tag…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {data.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
              <span>Tags:</span>
              {data.tags.map((t) => {
                const on = tagFilter.has(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleTagFilter(t)}
                    className={`rounded-full px-2 py-0.5 ${tagChipClasses(t, { active: on })}`}
                  >
                    {t}
                  </button>
                );
              })}
              {tagFilter.size > 0 && (
                <button
                  onClick={() => setTagFilter(new Set())}
                  className="text-slate-500 underline hover:text-slate-300"
                >
                  clear
                </button>
              )}
            </div>
          )}
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={showNotes}
              onChange={(e) => setShowNotes(e.target.checked)}
            />
            Show notes
          </label>
          <div className="text-xs text-slate-500">
            {shown.length} / {players.length}
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <SortHeader label="Name" k="name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Source server" k="source" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="Origin alliance" k="alliance" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortHeader label="THP" k="thp" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <th className="px-3 py-2">Tags</th>
                <SortHeader label="Seat" k="seat" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="center" />
                <th className="px-3 py-2">Target alliance</th>
                <SortHeader label="Conf." k="confirmed" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="center" />
                <SortHeader label="Acc." k="accepted" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="center" />
                <SortHeader label="Added" k="added" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {shown.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-6 text-center text-slate-500">
                    No players match.
                  </td>
                </tr>
              )}
              {shown.map((p) => (
                <Fragment key={p.id}>
                  <PlayerRow
                    player={p}
                    existingTags={data.tags}
                    windowId={windowId}
                    alliances={alliancesQuery.data ?? []}
                  />
                  {showNotes && p.notes && <NotesRow notes={p.notes} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <LeaverSection
        windowId={windowId}
        leavers={data.leavers}
        allPlayers={playersQuery.data ?? []}
      />

      <ServerOverviewSection rows={data.serverOverview} seatBuckets={data.seatBuckets} />

      <TagCounterSection
        tagStats={data.tagStats}
        seatBuckets={data.seatBuckets}
        window={data.window}
      />

      <Style />
    </div>
  );
}

function cmp(a: TransferPlayer, b: TransferPlayer, key: SortKey, dir: 'asc' | 'desc'): number {
  const mul = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name) * mul;
    case 'source':
      return a.sourceServer.localeCompare(b.sourceServer) * mul;
    case 'alliance':
      return (a.originAlliance ?? '').localeCompare(b.originAlliance ?? '') * mul;
    case 'thp': {
      const av = a.thp ?? -Infinity;
      const bv = b.thp ?? -Infinity;
      return (av - bv) * mul;
    }
    case 'confirmed':
      return (Number(a.confirmed) - Number(b.confirmed)) * mul;
    case 'accepted':
      return (Number(a.accepted) - Number(b.accepted)) * mul;
    case 'seat': {
      const av = a.seatColor ? SEAT_SORT_INDEX[a.seatColor] : 99;
      const bv = b.seatColor ? SEAT_SORT_INDEX[b.seatColor] : 99;
      return (av - bv) * mul;
    }
    case 'added':
      return a.createdAt.localeCompare(b.createdAt) * mul;
  }
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
  align,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onClick: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const active = sortKey === k;
  return (
    <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''}`}>
      <button
        onClick={() => onClick(k)}
        className={`inline-flex items-center gap-1 uppercase ${active ? 'text-white' : 'hover:text-slate-200'}`}
      >
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>
    </th>
  );
}

function WindowEditor({ detail }: { detail: TransferWindowDetail }) {
  const [open, setOpen] = useState(false);
  const w = detail.window;
  const [name, setName] = useState(w.name);
  const [notes, setNotes] = useState(w.notes ?? '');
  const [gold, setGold] = useState(w.seatsGold?.toString() ?? '');
  const [purple, setPurple] = useState(w.seatsPurple?.toString() ?? '');
  const [blue, setBlue] = useState(w.seatsBlue?.toString() ?? '');
  const [white, setWhite] = useState(w.seatsWhite?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const parseSeat = (v: string): number | null =>
    v.trim() === '' ? null : Number.isInteger(Number(v)) && Number(v) >= 0 ? Number(v) : null;

  const save = useMutation({
    mutationFn: () =>
      api.updateTransferWindow(w.id, {
        name: name.trim(),
        notes: notes.trim() || null,
        seatsGold: parseSeat(gold),
        seatsPurple: parseSeat(purple),
        seatsBlue: parseSeat(blue),
        seatsWhite: parseSeat(white),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-window', w.id] });
      qc.invalidateQueries({ queryKey: ['transfer-windows'] });
      setOpen(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const seatValues: { key: TransferSeatColor; value: number | null }[] = [
    { key: 'gold', value: w.seatsGold },
    { key: 'purple', value: w.seatsPurple },
    { key: 'blue', value: w.seatsBlue },
    { key: 'white', value: w.seatsWhite },
  ];

  if (!open) {
    return (
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Seats</span>
          {seatValues.map(({ key, value }) => (
            <SeatCountChip key={key} color={key} value={value} />
          ))}
          {w.notes && (
            <span className="basis-full text-slate-400">{w.notes}</span>
          )}
        </div>
        <button
          onClick={() => setOpen(true)}
          className="shrink-0 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
        >
          Edit window
        </button>
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,90px))]">
        <label className="flex flex-col gap-1 text-xs text-slate-400 md:col-span-1">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {(
          [
            ['gold', gold, setGold],
            ['purple', purple, setPurple],
            ['blue', blue, setBlue],
            ['white', white, setWhite],
          ] as const
        ).map(([key, value, set]) => (
          <label key={key} className="flex flex-col gap-1 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1">
              <Armchair size={12} className={SEAT_COLOR_STYLE[key].chair} />
              {SEAT_COLOR_STYLE[key].label}
            </span>
            <input
              className="input"
              inputMode="numeric"
              placeholder="—"
              value={value}
              onChange={(e) => set(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </label>
        ))}
      </div>
      <textarea
        className="input"
        placeholder="Notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setOpen(false)}
          className="rounded border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800"
        >
          Cancel
        </button>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary">
          Save
        </button>
      </div>
    </section>
  );
}

function AddPlayerForm({ windowId, existingTags }: { windowId: number; existingTags: string[] }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [sourceServer, setSourceServer] = useState('');
  const [originAlliance, setOriginAlliance] = useState('');
  const [thp, setThp] = useState('');
  const [notes, setNotes] = useState('');
  const [seatColor, setSeatColor] = useState<TransferSeatColor | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api.addTransferPlayer(windowId, {
        name: name.trim(),
        sourceServer: sourceServer.trim(),
        originAlliance: originAlliance.trim() || null,
        thp: thp.trim() ? Number(thp) : null,
        notes: notes.trim() || null,
        seatColor,
        tags,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });
      setName('');
      setSourceServer((prev) => prev);
      setOriginAlliance('');
      setThp('');
      setNotes('');
      setSeatColor(null);
      setTags([]);
      setTagInput('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const commitTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t) return;
    setTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagInput('');
  };

  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag(tagInput);
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1));
    }
  };

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Add player</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (tagInput.trim()) commitTag(tagInput);
          if (!name.trim() || !sourceServer.trim()) return;
          add.mutate();
        }}
        className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,3fr)_auto_minmax(0,1fr)_auto] md:items-end"
      >
        <FieldLabel label="Name" required>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </FieldLabel>
        <FieldLabel label="Source server" required>
          <input
            className="input"
            value={sourceServer}
            onChange={(e) => setSourceServer(e.target.value)}
            required
          />
        </FieldLabel>
        <FieldLabel label="Source alliance">
          <input
            className="input"
            value={originAlliance}
            onChange={(e) => setOriginAlliance(e.target.value)}
          />
        </FieldLabel>
        <FieldLabel label="Tags">
          <TagInput
            tags={tags}
            value={tagInput}
            onValue={setTagInput}
            onKeyDown={onTagKey}
            onRemove={(t) => setTags((prev) => prev.filter((x) => x !== t))}
            suggestions={existingTags.filter((t) => !tags.includes(t))}
          />
        </FieldLabel>
        <FieldLabel label="Seat">
          <div className="flex h-[34px] items-center rounded border border-slate-700 bg-slate-950 px-1">
            <SeatPicker value={seatColor} onChange={setSeatColor} />
          </div>
        </FieldLabel>
        <FieldLabel label="THP">
          <input
            className="input"
            inputMode="decimal"
            value={thp}
            onChange={(e) => setThp(e.target.value.replace(/[^0-9.]/g, ''))}
          />
        </FieldLabel>
        <button className="btn-primary h-[34px]" disabled={add.isPending}>
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
        <div className="md:col-span-7">
          <FieldLabel label="Notes">
            <input
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FieldLabel>
        </div>
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

function FieldLabel({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-slate-400">
      <span>
        {label}
        {required && <span className="text-slate-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

function TagInput({
  tags,
  value,
  onValue,
  onKeyDown,
  onRemove,
  suggestions,
}: {
  tags: string[];
  value: string;
  onValue: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onRemove: (t: string) => void;
  suggestions: string[];
}) {
  const listId = useMemo(() => `tag-suggest-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 focus-within:border-slate-500">
      {tags.map((t) => (
        <span
          key={t}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${tagChipClasses(t)}`}
        >
          {t}
          <button
            type="button"
            onClick={() => onRemove(t)}
            className="opacity-70 hover:opacity-100"
            aria-label={`Remove tag ${t}`}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        className="min-w-[6ch] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none"
        placeholder={tags.length === 0 ? 'Tags…' : ''}
        value={value}
        onChange={(e) => onValue(e.target.value)}
        onKeyDown={onKeyDown}
        list={listId}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}

function PlayerRow({
  player,
  existingTags,
  windowId,
  alliances,
}: {
  player: TransferPlayer;
  existingTags: string[];
  windowId: number;
  alliances: Alliance[];
}) {
  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();

  const invalidate = () => qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });

  const patchFlag = useMutation({
    mutationFn: (body: { confirmed?: boolean; accepted?: boolean }) =>
      api.updateTransferPlayer(player.id, body),
    onSuccess: invalidate,
  });

  const patchSeat = useMutation({
    mutationFn: (seatColor: TransferSeatColor | null) =>
      api.updateTransferPlayer(player.id, { seatColor }),
    onSuccess: invalidate,
  });

  const patchAlliance = useMutation({
    mutationFn: (targetAllianceId: number | null) =>
      api.updateTransferPlayer(player.id, { targetAllianceId }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteTransferPlayer(player.id),
    onSuccess: invalidate,
  });

  if (editing) {
    return (
      <PlayerEditor
        player={player}
        existingTags={existingTags}
        onDone={() => {
          setEditing(false);
          invalidate();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <tr>
      <td className="px-3 py-2 font-medium">{player.name}</td>
      <td className="px-3 py-2 text-slate-300">{player.sourceServer}</td>
      <td className="px-3 py-2 text-slate-300">{player.originAlliance ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {player.thp == null ? '—' : formatThp(player.thp)}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {player.tags.length === 0 ? (
            <span className="text-xs text-slate-600">—</span>
          ) : (
            player.tags.map((t) => (
              <span
                key={t}
                className={`rounded-full px-2 py-0.5 text-xs ${tagChipClasses(t)}`}
              >
                {t}
              </span>
            ))
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <SeatPicker
          value={player.seatColor}
          onChange={(c) => patchSeat.mutate(c)}
          disabled={patchSeat.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <AllianceSelect
          alliances={alliances}
          value={player.targetAllianceId}
          onChange={(v) => patchAlliance.mutate(v)}
          disabled={patchAlliance.isPending}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={player.confirmed}
          onChange={(e) => patchFlag.mutate({ confirmed: e.target.checked })}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={player.accepted}
          onChange={(e) => patchFlag.mutate({ accepted: e.target.checked })}
        />
      </td>
      <td className="px-3 py-2 text-xs text-slate-500" title={player.createdAt}>
        {player.createdAt.slice(0, 10)}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setEditing(true)}
            className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            Edit
          </button>
          <button
            onClick={() => {
              if (confirm(`Remove ${player.name} from this window?`)) remove.mutate();
            }}
            className="rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function SeatCountChip({
  color,
  value,
}: {
  color: TransferSeatColor;
  value: number | null;
}) {
  const s = SEAT_COLOR_STYLE[color];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900/70 px-2 py-0.5 text-sm">
      <Armchair size={14} className={s.chair} />
      <span className="text-xs text-slate-400">{s.label}</span>
      <span className="font-medium tabular-nums text-slate-100">{value ?? '—'}</span>
    </span>
  );
}

function SeatPicker({
  value,
  onChange,
  disabled,
}: {
  value: TransferSeatColor | null;
  onChange: (next: TransferSeatColor | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const chairClass = value ? SEAT_COLOR_STYLE[value].chair : 'text-slate-600';
  const title = value ? `Seat: ${SEAT_COLOR_STYLE[value].label}` : 'No seat assigned';

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={title}
        aria-label={title}
        className="inline-flex items-center justify-center rounded p-1 hover:bg-slate-800 disabled:opacity-50"
      >
        <Armchair size={18} className={chairClass} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2 rounded-md border border-slate-700 bg-slate-900 p-1 shadow-lg"
        >
          <div className="flex items-center gap-1">
            {SEAT_COLOR_ORDER.map((c) => {
              const s = SEAT_COLOR_STYLE[c];
              const active = value === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                  }}
                  title={s.label}
                  aria-label={s.label}
                  className={`h-5 w-5 rounded-full border border-black/20 ${s.pill} ${
                    active ? `ring-2 ring-offset-1 ring-offset-slate-900 ${s.ring}` : ''
                  }`}
                />
              );
            })}
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              title="Clear"
              aria-label="Clear seat"
              className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NotesRow({ notes }: { notes: string }) {
  return (
    <tr className="bg-slate-950/50">
      <td colSpan={11} className="px-3 pb-2 pt-0 text-xs text-slate-400">
        <span className="text-slate-600">Notes:</span> {notes}
      </td>
    </tr>
  );
}

function PlayerEditor({
  player,
  existingTags,
  onDone,
  onCancel,
}: {
  player: TransferPlayer;
  existingTags: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(player.name);
  const [sourceServer, setSourceServer] = useState(player.sourceServer);
  const [originAlliance, setOriginAlliance] = useState(player.originAlliance ?? '');
  const [thp, setThp] = useState(player.thp?.toString() ?? '');
  const [notes, setNotes] = useState(player.notes ?? '');
  const [seatColor, setSeatColor] = useState<TransferSeatColor | null>(player.seatColor);
  const [tags, setTags] = useState<string[]>(player.tags);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateTransferPlayer(player.id, {
        name: name.trim(),
        sourceServer: sourceServer.trim(),
        originAlliance: originAlliance.trim() || null,
        thp: thp.trim() ? Number(thp) : null,
        notes: notes.trim() || null,
        seatColor,
        tags,
      }),
    onSuccess: onDone,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const commitTag = (raw: string) => {
    const t = raw.trim().replace(/,+$/, '');
    if (!t) return;
    setTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagInput('');
  };

  return (
    <tr className="bg-slate-900/40">
      <td className="px-3 py-2">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </td>
      <td className="px-3 py-2">
        <input
          className="input"
          value={sourceServer}
          onChange={(e) => setSourceServer(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <input
          className="input"
          value={originAlliance}
          onChange={(e) => setOriginAlliance(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <input
          className="input text-right"
          inputMode="decimal"
          value={thp}
          onChange={(e) => setThp(e.target.value.replace(/[^0-9.]/g, ''))}
        />
      </td>
      <td className="px-3 py-2">
        <TagInput
          tags={tags}
          value={tagInput}
          onValue={setTagInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commitTag(tagInput);
            } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
              setTags((prev) => prev.slice(0, -1));
            }
          }}
          onRemove={(t) => setTags((prev) => prev.filter((x) => x !== t))}
          suggestions={existingTags.filter((t) => !tags.includes(t))}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <SeatPicker value={seatColor} onChange={setSeatColor} />
      </td>
      <td className="px-3 py-2 text-center text-slate-500" colSpan={4}>
        <textarea
          className="input"
          rows={2}
          placeholder="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="btn-primary"
          >
            Save
          </button>
          <button
            onClick={onCancel}
            className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

function formatThp(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toString();
}

function formatDelta(v: number): string {
  const sign = v > 0 ? '+' : v < 0 ? '−' : '±';
  return `${sign}${formatThp(Math.abs(v))}`;
}

function StatsTiles({ stats }: { stats: TransferStats }) {
  const currentDelta = stats.projected.thpSum - stats.current.thpSum;
  const countDelta = stats.projected.count - stats.current.count;
  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
      <Tile
        label="Current roster"
        accent="text-slate-100"
        primary={`${stats.current.count} · ${formatThp(stats.current.thpSum)}`}
        secondary={`Ø ${formatThp(stats.current.thpAvg)}`}
      />
      <Tile
        label="Leavers"
        accent="text-amber-300"
        primary={`${stats.leavers.count} · ${formatThp(stats.leavers.thpSum)}`}
        secondary={`Ø ${formatThp(stats.leavers.thpAvg)} · ${stats.leavers.confirmed} confirmed`}
      />
      <Tile
        label="Incoming"
        accent="text-emerald-300"
        primary={`${stats.incoming.count} · ${formatThp(stats.incoming.thpSum)}`}
        secondary={`Ø ${formatThp(stats.incoming.thpAvg)} · ${stats.incoming.accepted} accepted (${formatThp(stats.incoming.thpAcceptedSum)}, Ø ${formatThp(stats.incoming.thpAcceptedAvg)})`}
      />
      <Tile
        label="After transfer"
        accent="text-indigo-300"
        primary={`${stats.projected.count} · ${formatThp(stats.projected.thpSum)}`}
        secondary={`Ø ${formatThp(stats.projected.thpAvg)} · Δ ${countDelta >= 0 ? '+' : ''}${countDelta} / ${formatDelta(currentDelta)}`}
      />
    </section>
  );
}

function Tile({
  label,
  primary,
  secondary,
  accent,
}: {
  label: string;
  primary: string;
  secondary: string;
  accent: string;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${accent}`}>{primary}</div>
      <div className="mt-0.5 text-xs text-slate-400">{secondary}</div>
    </div>
  );
}

function AllianceSelect({
  alliances,
  value,
  onChange,
  disabled,
}: {
  alliances: Alliance[];
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  const active = alliances.filter((a) => a.active);
  return (
    <select
      className="input py-1 text-xs"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      disabled={disabled}
    >
      <option value="">—</option>
      {active.map((a) => (
        <option key={a.id} value={a.id}>
          {a.tag ? `[${a.tag}] ` : ''}
          {a.name}
        </option>
      ))}
    </select>
  );
}

function LeaverSection({
  windowId,
  leavers,
  allPlayers,
}: {
  windowId: number;
  leavers: TransferPlayer[];
  allPlayers: Player[];
}) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });

  const bulkAdd = useMutation({
    mutationFn: (ids: number[]) => api.bulkCreateLeavers(windowId, ids),
    onSuccess: () => {
      setPicked(new Set());
      setShowPicker(false);
      invalidate();
    },
  });

  const leaverPlayerIds = new Set(leavers.map((l) => l.playerId).filter((v): v is number => v != null));
  const eligible = allPlayers.filter(
    (p) => !leaverPlayerIds.has(p.id) && p.active && !p.deleted,
  );
  const filtered = search.trim()
    ? eligible.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : eligible;

  const togglePick = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-400">
          <UserMinus size={16} /> Leavers ({leavers.length})
        </h3>
        <button
          onClick={() => setShowPicker((v) => !v)}
          className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
        >
          {showPicker ? 'Close picker' : 'Add leavers…'}
        </button>
      </div>

      {showPicker && (
        <div className="space-y-2 rounded border border-slate-800 bg-slate-950/40 p-3">
          <input
            className="input max-w-sm"
            placeholder="Search roster…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-72 overflow-y-auto rounded border border-slate-800">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-800">
                {filtered.length === 0 && (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-500">
                      No matching active players.
                    </td>
                  </tr>
                )}
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => togglePick(p.id)}
                    className={`cursor-pointer hover:bg-slate-900 ${picked.has(p.id) ? 'bg-slate-900/70' : ''}`}
                  >
                    <td className="w-8 px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={picked.has(p.id)}
                        onChange={() => togglePick(p.id)}
                      />
                    </td>
                    <td className="px-3 py-1.5">{p.name}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{p.rank}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                      {p.latestSnapshot ? formatThp(p.latestSnapshot.totalPower) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{picked.size} selected</span>
            <button
              disabled={picked.size === 0 || bulkAdd.isPending}
              onClick={() => bulkAdd.mutate(Array.from(picked))}
              className="btn-primary"
            >
              {bulkAdd.isPending ? 'Adding…' : `Add ${picked.size} leaver${picked.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded border border-slate-800">
        <table className="w-full min-w-[600px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 text-right">THP</th>
              <th className="px-3 py-2">Comment</th>
              <th className="px-3 py-2 text-center">Conf.</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {leavers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-slate-500">
                  No leavers yet.
                </td>
              </tr>
            )}
            {leavers.map((l) => (
              <LeaverRow key={l.id} leaver={l} windowId={windowId} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LeaverRow({ leaver, windowId }: { leaver: TransferPlayer; windowId: number }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(leaver.notes ?? '');
  const [dirty, setDirty] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });

  const patch = useMutation({
    mutationFn: (body: { confirmed?: boolean; notes?: string | null }) =>
      api.updateTransferPlayer(leaver.id, body),
    onSuccess: () => {
      setDirty(false);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTransferPlayer(leaver.id),
    onSuccess: invalidate,
  });

  return (
    <tr>
      <td className="px-3 py-2 font-medium">
        {leaver.playerId ? (
          <Link to={`/players/${leaver.playerId}`} className="hover:text-white">
            {leaver.name}
          </Link>
        ) : (
          leaver.name
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {leaver.thp == null ? '—' : formatThp(leaver.thp)}
      </td>
      <td className="px-3 py-2">
        <input
          className="input py-1 text-xs"
          value={notes}
          placeholder="Comment…"
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
          onBlur={() => {
            if (dirty) patch.mutate({ notes: notes.trim() || null });
          }}
        />
      </td>
      <td className="px-3 py-2 text-center">
        <input
          type="checkbox"
          checked={leaver.confirmed}
          onChange={(e) => patch.mutate({ confirmed: e.target.checked })}
        />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => {
            if (confirm(`Remove ${leaver.name} as leaver?`)) remove.mutate();
          }}
          className="rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

const SEAT_BUCKET_ORDER: TransferSeatBucket[] = ['gold', 'purple', 'blue', 'white', 'other'];

function seatBucketLabel(b: TransferSeatBucket): string {
  return b === 'other' ? 'Other' : SEAT_COLOR_STYLE[b].label;
}

function SeatChairIcon({ bucket, size = 14 }: { bucket: TransferSeatBucket; size?: number }) {
  if (bucket === 'other') return <Armchair size={size} className="text-slate-500" />;
  return <Armchair size={size} className={SEAT_COLOR_STYLE[bucket].chair} />;
}

function ServerOverviewSection({
  rows,
  seatBuckets,
}: {
  rows: TransferAllianceOverviewRow[];
  seatBuckets: TransferSeatBucket[];
}) {
  const order = seatBuckets.length ? seatBuckets : SEAT_BUCKET_ORDER;
  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-400">
        <Users size={16} /> Server overview — incoming by target alliance
      </h3>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500">No alliances configured yet.</div>
      ) : (
        <div className="overflow-x-auto rounded border border-slate-800">
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-3 py-2">Alliance</th>
                <th className="px-3 py-2 text-center">Players</th>
                {order.map((b) => (
                  <th key={b} className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <SeatChairIcon bucket={b} />
                      <span className="text-[10px]">{seatBucketLabel(b)}</span>
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 text-center">Conf.</th>
                <th className="px-3 py-2 text-center">Acc.</th>
                <th className="px-3 py-2 text-right">THP Σ</th>
                <th className="px-3 py-2 text-right">THP Ø</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => (
                <tr key={r.allianceId}>
                  <td className="px-3 py-2 font-medium">
                    {r.tag && <span className="mr-1 text-slate-500">[{r.tag}]</span>}
                    {r.name}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">{r.count}</td>
                  {order.map((b) => (
                    <td key={b} className="px-2 py-2 text-center tabular-nums text-slate-300">
                      {r.seats[b] || <span className="text-slate-700">·</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center tabular-nums">{r.confirmed}</td>
                  <td className="px-3 py-2 text-center tabular-nums">{r.accepted}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.thpSum ? formatThp(r.thpSum) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-400">
                    {r.count ? formatThp(r.thpAvg) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TagCounterSection({
  tagStats,
  seatBuckets,
  window,
}: {
  tagStats: TransferTagStat[];
  seatBuckets: TransferSeatBucket[];
  window: TransferWindowDetail['window'];
}) {
  const order = seatBuckets.length ? seatBuckets : SEAT_BUCKET_ORDER;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (t: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  const combined = useMemo(() => {
    const acc = {
      count: 0,
      confirmed: 0,
      accepted: 0,
      seats: { gold: 0, purple: 0, blue: 0, white: 0, other: 0 } as Record<
        TransferSeatBucket,
        number
      >,
      thpSum: 0,
    };
    for (const s of tagStats) {
      if (!selected.has(s.tag)) continue;
      acc.count += s.count;
      acc.confirmed += s.confirmed;
      acc.accepted += s.accepted;
      acc.thpSum += s.thpSum;
      for (const b of order) acc.seats[b] += s.seats[b] ?? 0;
    }
    return acc;
  }, [selected, tagStats, order]);

  const seatCap: Record<TransferSeatBucket, number | null> = {
    gold: window.seatsGold,
    purple: window.seatsPurple,
    blue: window.seatsBlue,
    white: window.seatsWhite,
    other: null,
  };

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
          Tag counters
        </h3>
        {selected.size > 0 && (
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-slate-500 underline hover:text-slate-300"
          >
            clear selection
          </button>
        )}
      </div>

      {tagStats.length === 0 ? (
        <div className="text-sm text-slate-500">No tags in this window yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {tagStats.map((s) => {
              const on = selected.has(s.tag);
              return (
                <button
                  key={s.tag}
                  onClick={() => toggle(s.tag)}
                  className={`rounded-lg border p-3 text-left transition ${on ? 'border-indigo-500 bg-indigo-950/40' : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${tagChipClasses(s.tag, { active: on })}`}>
                      {s.tag}
                    </span>
                    <span className="text-lg font-semibold tabular-nums">{s.count}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                    {order.map((b) => (
                      <span key={b} className="inline-flex items-center gap-0.5">
                        <SeatChairIcon bucket={b} size={12} />
                        <span className="tabular-nums">{s.seats[b] || 0}</span>
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {s.confirmed} conf. · {s.accepted} acc. · Σ {s.thpSum ? formatThp(s.thpSum) : '—'}
                  </div>
                </button>
              );
            })}
          </div>

          {selected.size > 0 && (
            <div className="rounded-lg border border-indigo-800 bg-indigo-950/30 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs uppercase tracking-wider text-indigo-200">
                  Combined ({selected.size} tag{selected.size === 1 ? '' : 's'})
                </div>
                <div className="text-sm tabular-nums text-slate-200">
                  {combined.count} players · {combined.confirmed} confirmed · {combined.accepted}{' '}
                  accepted · Σ {combined.thpSum ? formatThp(combined.thpSum) : '—'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {order.map((b) => {
                  const used = combined.seats[b];
                  const cap = seatCap[b];
                  const remaining = cap == null ? null : cap - used;
                  const overflow = remaining != null && remaining < 0;
                  return (
                    <div
                      key={b}
                      className="rounded border border-slate-800 bg-slate-950/50 p-2 text-xs"
                    >
                      <div className="flex items-center gap-1 text-slate-400">
                        <SeatChairIcon bucket={b} />
                        <span>{seatBucketLabel(b)}</span>
                      </div>
                      <div className="mt-1 flex items-baseline justify-between tabular-nums">
                        <span className="text-sm font-semibold text-slate-100">{used}</span>
                        <span className="text-[10px] text-slate-500">
                          {cap == null ? 'no cap' : `/${cap}`}
                        </span>
                      </div>
                      {cap != null && (
                        <div
                          className={`mt-0.5 text-[11px] ${overflow ? 'text-red-400' : 'text-emerald-300'}`}
                        >
                          {overflow ? `${remaining} over` : `${remaining} free`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
